import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';
import { useOfflineStorage } from '@/hooks/useOfflineStorage';

export interface ObligacionAmbiental {
  id: string;
  codigo: string;
  nombre_programa: string;
  tipo: 'Ambiental' | 'Social' | 'Biótico' | 'Abiótico' | 'SST';
  objetivo: string;
  frecuencia: 'Continuo' | 'Mensual' | 'Trimestral' | 'Anual' | 'Única';
  entidad_reporte: string;
  responsable_interno: string;
  estado: 'Pendiente' | 'En Proceso' | 'Cumplido' | 'Vencido';
  vencimiento: string;
  indicadores: string[];
  nivel_cumplimiento: number;
  plazo_cumplimiento: string;
  observaciones?: string;
  evidencia?: string;
  created_at: string;
  updated_at: string;
}

export const useObligaciones = (tipoFiltro?: 'Ambiental' | 'Social' | 'SST' | 'Biótico' | 'Abiótico') => {
  const [obligaciones, setObligaciones] = useState<ObligacionAmbiental[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { user } = useAuth();
  const { toast } = useToast();
  const { cacheData, getCachedData, addPendingOperation, isOnline } = useOfflineStorage();

  const loadObligaciones = async () => {
    if (!user) return;
    
    try {
      setLoading(true);
      
      if (!isOnline) {
        // Load from cache when offline
        const cachedData = await getCachedData('obligaciones', 'all');
        if (cachedData) {
          setObligaciones(cachedData);
          setError(null);
        }
        // Si no hay caché, mantener los datos actuales (no limpiar)
        return;
      }

      let query = supabase
        .from('obligaciones_ambientales')
        .select('*');
      
      // Aplicar filtro por tipo si se especifica
      if (tipoFiltro) {
        // Para Ambiental, incluir también Biótico y Abiótico
        if (tipoFiltro === 'Ambiental') {
          query = query.in('tipo', ['Ambiental', 'Biótico', 'Abiótico']);
        } else {
          query = query.eq('tipo', tipoFiltro);
        }
      }
      
      const { data, error } = await query.order('created_at', { ascending: false });

      if (error) throw error;

      const formattedData: ObligacionAmbiental[] = data.map(item => ({
        id: item.id,
        codigo: item.codigo,
        nombre_programa: item.nombre_programa,
        tipo: item.tipo,
        objetivo: item.objetivo || '',
        frecuencia: item.frecuencia,
        entidad_reporte: item.entidad_reporte || '',
        responsable_interno: item.responsable_interno || '',
        estado: item.estado,
        vencimiento: item.vencimiento || '',
        indicadores: item.indicadores || [],
        nivel_cumplimiento: item.nivel_cumplimiento || 0,
        plazo_cumplimiento: item.plazo_cumplimiento || '',
        observaciones: item.observaciones || '',
        evidencia: item.evidencia || '',
        created_at: item.created_at,
        updated_at: item.updated_at,
      }));

      setObligaciones(formattedData);
      await cacheData('obligaciones', 'all', formattedData);
      setError(null);
    } catch (err: any) {
      setError(err.message);
      // Try loading from cache on error
      const cachedData = await getCachedData('obligaciones', 'all');
      if (cachedData) {
        setObligaciones(cachedData);
        toast({
          title: "Modo offline",
          description: "Mostrando datos guardados localmente",
        });
      }
      // Si no hay caché, mantener los datos actuales (no mostrar error si hay datos previos)
      if (!cachedData && obligaciones.length === 0) {
        toast({
          title: "Error",
          description: "No se pudieron cargar las obligaciones",
          variant: "destructive",
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const createObligacion = async (obligacion: Omit<ObligacionAmbiental, 'id' | 'created_at' | 'updated_at'>) => {
    if (!user) return;

    try {
      if (!isOnline) {
        // Create temporary ID for offline operation
        const tempId = `temp-${Date.now()}-${Math.random()}`;
        const tempObligacion: ObligacionAmbiental = {
          ...obligacion,
          id: tempId,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        };

        // Update UI immediately (optimistic update)
        const updatedObligaciones = [tempObligacion, ...obligaciones];
        setObligaciones(updatedObligaciones);
        await cacheData('obligaciones', 'all', updatedObligaciones);

        // Queue operation for sync
        await addPendingOperation('create', 'obligaciones_ambientales', {
          ...obligacion,
          user_id: user.id,
          _tempId: tempId, // Store temp ID for later update
        });

        toast({
          title: "Guardado offline",
          description: "Se sincronizará cuando vuelva la conexión",
        });
        return tempObligacion;
      }

      const { data, error } = await supabase
        .from('obligaciones_ambientales')
        .insert([{
          codigo: obligacion.codigo,
          nombre_programa: obligacion.nombre_programa,
          tipo: obligacion.tipo,
          objetivo: obligacion.objetivo,
          frecuencia: obligacion.frecuencia,
          entidad_reporte: obligacion.entidad_reporte,
          responsable_interno: obligacion.responsable_interno,
          estado: obligacion.estado,
          vencimiento: obligacion.vencimiento || null,
          indicadores: obligacion.indicadores,
          nivel_cumplimiento: obligacion.nivel_cumplimiento,
          plazo_cumplimiento: obligacion.plazo_cumplimiento,
          observaciones: obligacion.observaciones,
          evidencia: obligacion.evidencia,
          user_id: user.id,
        }])
        .select()
        .single();

      if (error) throw error;

      await loadObligaciones();
      
      toast({
        title: "Éxito",
        description: "Obligación creada correctamente",
      });

      return data;
    } catch (err: any) {
      toast({
        title: "Error",
        description: err.message || "No se pudo crear la obligación",
        variant: "destructive",
      });
      throw err;
    }
  };

  const updateObligacion = async (id: string, updates: Partial<ObligacionAmbiental>) => {
    if (!user) return;

    try {
      if (!isOnline) {
        // Update UI immediately (optimistic update)
        const updatedObligaciones = obligaciones.map(o => 
          o.id === id ? { ...o, ...updates, updated_at: new Date().toISOString() } : o
        );
        setObligaciones(updatedObligaciones);
        await cacheData('obligaciones', 'all', updatedObligaciones);

        // Queue operation for sync
        await addPendingOperation('update', 'obligaciones_ambientales', { id, ...updates });
        toast({
          title: "Guardado offline",
          description: "Se sincronizará cuando vuelva la conexión",
        });
        return;
      }

      // Whitelist de campos permitidos y normalización
      const allowed = [
        'codigo','nombre_programa','tipo','objetivo','frecuencia','entidad_reporte','responsable_interno',
        'estado','vencimiento','indicadores','nivel_cumplimiento','plazo_cumplimiento','observaciones','evidencia','user_id'
      ] as const;
      const sanitized: Record<string, any> = {};
      for (const k of allowed) {
        if (k in updates) (sanitized as any)[k] = (updates as any)[k];
      }
      // Siempre intentamos asignar el user_id para "reclamar" filas huérfanas (user_id NULL)
      sanitized.user_id = user.id;
      if ('vencimiento' in sanitized) {
        sanitized.vencimiento = sanitized.vencimiento ? sanitized.vencimiento : null;
      }

      const { data, error } = await supabase
        .from('obligaciones_ambientales')
        .update(sanitized)
        .eq('id', id)
        .select()
        .maybeSingle();

      if (error) throw error;
      if (!data) throw new Error('No se encontró la obligación o no tiene permisos para actualizarla');

      await loadObligaciones();
      toast({
        title: "Éxito",
        description: "Obligación actualizada correctamente",
      });
    } catch (err: any) {
      toast({
        title: "Error",
        description: err.message || "No se pudo actualizar la obligación",
        variant: "destructive",
      });
      throw err;
    }
  };

  const deleteObligacion = async (id: string) => {
    if (!user) return;

    try {
      if (!isOnline) {
        // Update UI immediately (optimistic update)
        const updatedObligaciones = obligaciones.filter(o => o.id !== id);
        setObligaciones(updatedObligaciones);
        await cacheData('obligaciones', 'all', updatedObligaciones);

        // Queue operation for sync
        await addPendingOperation('delete', 'obligaciones_ambientales', { id });
        toast({
          title: "Guardado offline",
          description: "Se sincronizará cuando vuelva la conexión",
        });
        return;
      }

      const { error } = await supabase
        .from('obligaciones_ambientales')
        .delete()
        .eq('id', id)
        .eq('user_id', user.id);

      if (error) throw error;

      await loadObligaciones();
      
      toast({
        title: "Éxito",
        description: "Obligación eliminada correctamente",
      });
    } catch (err: any) {
      toast({
        title: "Error",
        description: err.message || "No se pudo eliminar la obligación",
        variant: "destructive",
      });
      throw err;
    }
  };

  useEffect(() => {
    if (user) {
      loadObligaciones();
    }
  }, [user, tipoFiltro]);

  // Escuchar eventos de sincronización offline para recargar datos
  useEffect(() => {
    const handleSyncComplete = (event: CustomEvent) => {
      const { tables } = event.detail;
      if (tables.includes('obligaciones_ambientales')) {
        console.log('Recargando obligaciones después de sincronización');
        loadObligaciones();
      }
    };

    window.addEventListener('offline-sync-complete', handleSyncComplete as EventListener);
    return () => {
      window.removeEventListener('offline-sync-complete', handleSyncComplete as EventListener);
    };
  }, [user]);

  // Suscripción a cambios en tiempo real para TODOS los usuarios
  useEffect(() => {
    if (!user) return;

    const channel = supabase
      .channel('obligaciones-realtime-all')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'obligaciones_ambientales'
        },
        (payload) => {
          console.log('Realtime change (all users):', payload);
          // Recargar datos cuando hay cambios de cualquier usuario
          loadObligaciones();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user]);

  const getEstadisticas = () => {
    const total = obligaciones.length;
    const cumplidas = obligaciones.filter(o => o.estado === 'Cumplido').length;
    const enProceso = obligaciones.filter(o => o.estado === 'En Proceso').length;
    const pendientes = obligaciones.filter(o => o.estado === 'Pendiente').length;
    const vencidas = obligaciones.filter(o => o.estado === 'Vencido').length;

    // Calcular próximas a vencer (próximos 30 días)
    const today = new Date();
    const thirtyDaysFromNow = new Date();
    thirtyDaysFromNow.setDate(today.getDate() + 30);
    
    const proximasAVencer = obligaciones.filter(o => {
      if (o.estado === 'Cumplido' || o.estado === 'Vencido') return false;
      if (!o.vencimiento) return false;
      const vencimiento = new Date(o.vencimiento);
      return vencimiento >= today && vencimiento <= thirtyDaysFromNow;
    }).length;

    // Calcular porcentaje de cumplimiento total promediando los niveles individuales
    const porcentajeCumplimiento = total > 0 
      ? Math.round(obligaciones.reduce((suma, o) => suma + o.nivel_cumplimiento, 0) / total)
      : 0;

    return {
      total,
      cumplidas,
      enProceso,
      pendientes,
      vencidas,
      proximasAVencer,
      porcentajeCumplimiento
    };
  };

  return {
    obligaciones,
    loading,
    error,
    createObligacion,
    updateObligacion,
    deleteObligacion,
    loadObligaciones,
    getEstadisticas,
  };
};